/**
 * LinkedTableDataDisplay Component
 *
 * Displays linkedTable preview data with server-side pagination, sorting, and filtering.
 * Uses the POST /api/linkedTables/:id/preview endpoint via useDataGrid's customQueryFn.
 *
 * Features:
 * - Server-side pagination for large preview tables
 * - Real-time filtering and sorting via API
 * - Automatic column generation from schema
 * - Hidden columns management
 * - Preview configuration (filters, calculated columns, relationships)
 */

import React, { useMemo } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { Columns3, Loader2 } from 'lucide-react';
import type {
  LinkedTablePreviewResponse,
  LinkedTableFieldMetadata,
  LinkedTableConfigChanges,
  LinkedTableNormalizedConfig,
  LinkedTableCardinalityError,
} from '@loader/shared-types';

import { CardinalityWarning } from './CardinalityWarning';
import { QueryErrorDisplay } from './QueryErrorDisplay';

import { isFilterComplete, isRelationshipComplete } from '@/features/linkedTables/editLinkedTable/hooks/useLinkedTableEditor';
import { mapFieldMappingToCondition } from '@/features/linkedTables/editLinkedTable/utils/joinConditionMapper';
import {
  DataTable,
  useDataGrid,
  type DataGridRequest,
  type DataGridResponse,
} from '@/components/data-grid';
import { generateColumnsFromSchema } from '@/lib/tableHelpers';
import { useRpc } from '@/lib/rpc';
import type {
  LinkedTableCalculatedColumn,
  LinkedTableFilter,
  LinkedTableRelationship,
  LinkedTableValue,
  LinkedTableRowGroupSort,
  LinkedTableVariable,
} from '@/lib/types';

/**
 * Props for LinkedTableDataDisplay component
 */
interface LinkedTableDataDisplayProps {
  /** LinkedTable ID for preview endpoint */
  linkedTableId: string;
  /** Calculated columns configuration */
  calculated_columns: LinkedTableCalculatedColumn[];
  /** Row groups configuration */
  row_groups: string[];
  /** Row group sorts configuration (ORDER BY) */
  row_group_sorts: LinkedTableRowGroupSort[];
  /** Values configuration */
  values: LinkedTableValue[];
  /** LinkedTable metadata (for calculated column schema generation) */
  linkedTable: import('@/lib/types').LinkedTable | null;
  /** LinkedTable filters configuration for preview */
  filters: LinkedTableFilter[];
  /** LinkedTable relationships configuration for preview */
  relationships: LinkedTableRelationship[];
  /** Variables configuration for preview */
  variables?: LinkedTableVariable[];
  /** Unified version selections keyed by table alias */
  versionSelections?: Record<string, string>;
  /** Serialized config key for change tracking - prevents infinite re-renders */
  configKey: string;
  /** Callback to update parent component with fields including relationships */
  onFieldsChange?: (fields: import('@loader/shared-types').TableFieldApiResponse[]) => void;
  /** Callback when backend returns normalized config (config cleanup) */
  onConfigNormalized?: (normalizedConfig: LinkedTableNormalizedConfig, changes: LinkedTableConfigChanges) => void;
  /** Visual variant for DataTable: 'card' (default) or 'flush' */
  variant?: 'card' | 'flush';
}

/**
 * Extended preview response with field information
 */
interface PrelinkedTableData extends Record<string, unknown> {
  // Standard data grid records
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * LinkedTableDataDisplay Component
 *
 * Renders a server-side data table for linkedTable preview.
 */
export const LinkedTableDataDisplay: React.FC<LinkedTableDataDisplayProps> = ({
  linkedTableId,
  calculated_columns,
  row_groups,
  row_group_sorts,
  values,
  linkedTable,
  filters: linkedTableFilters,
  relationships,
  variables = [],
  versionSelections,
  configKey,
  onFieldsChange,
  onConfigNormalized,
  variant = 'card',
}) => {
  // ============================================================================
  // Hooks & Dependencies
  // ============================================================================

  const rpc = useRpc();

  // Store fields metadata from last successful query
  const [fieldsMetadata, setFieldsMetadata] = React.useState<LinkedTableFieldMetadata[]>([]);

  // Store cardinality error for inline display (instead of throwing to error boundary)
  const [cardinalityError, setCardinalityError] = React.useState<LinkedTableCardinalityError | null>(null);

  // Check for empty state
  const isEmpty = (!row_groups || row_groups.length === 0) && (!values || values.length === 0);

  /**
   * Filter out incomplete filters to prevent premature data updates
   * Only complete filters should be sent to the preview endpoint
   */
  const completeFilters = useMemo(() => {
    return linkedTableFilters.filter(isFilterComplete);
  }, [linkedTableFilters]);

  /**
   * Filter out incomplete relationships to prevent SQL generation errors
   * Only relationships with field_mappings configured should be sent to preview endpoint
   * This prevents SQL parse errors when relationships don't have JOIN conditions defined
   */
  const completeRelationships = useMemo(() => {
    return relationships.filter(isRelationshipComplete);
  }, [relationships]);

  // ============================================================================
  // Data Grid with Custom Query Function
  // ============================================================================

  /**
   * Memoize extraParams to ensure stable reference when configKey doesn't change
   * This prevents unnecessary query key changes in useDataGrid
   */
  const extraParams = React.useMemo(
    () => ({
      configKey,
      row_groups: JSON.stringify(row_groups),
      row_group_sorts: JSON.stringify(row_group_sorts),
      values: JSON.stringify(values),
    }),
    [configKey, row_groups, row_group_sorts, values]
  );

  /**
   * Use useDataGrid with custom query function for POST endpoint
   * This eliminates manual state management for pagination, sorting, and filtering
   * Always runs to ensure available_fields is fetched for the column picker
   */
  const grid = useDataGrid<PrelinkedTableData>({
    customQueryFn: async (params: DataGridRequest): Promise<DataGridResponse<PrelinkedTableData>> => {
      // Convert frontend relationships to SQL-native tables + joins format
      const tables = [];
      const joins = [];

      // Get base table from linkedTable
      if (linkedTable?.tables && linkedTable.tables.length > 0) {
        // Version selection is not stored in table definition
        // It's passed via table_versions query parameter below
        tables.push(linkedTable.tables[0]);

        // Convert each relationship to table + join
        for (const rel of completeRelationships) {
          // Add the joined table (version selection via query parameter)
          tables.push({
            id: rel.id,
            type: rel.target_type,
            target_id: rel.target_id,
            alias: rel.alias,
          });

          // Add the join
          joins.push({
            id: rel.id,
            left_table: linkedTable.tables[0].alias,
            right_table: rel.alias,
            join_type: (rel.join_type || 'left').toUpperCase() as 'LEFT' | 'INNER' | 'RIGHT' | 'FULL',
            conditions: (rel.field_mappings || []).map(mapFieldMappingToCondition),
          });
        }
      }

      // No auto-aggregation needed - row_groups and values are explicit

      // @ts-expect-error - Hono RPC type inference limitation
      const res = await rpc.api.private['linked-tables'][':id'].preview.$post({
        param: { id: linkedTableId },
        json: {
          config: {
            filters: completeFilters,
            calculated_columns,
            tables, // SQL-native: tables instead of relationships
            joins: joins.length > 0 ? joins : null, // SQL-native: joins instead of relationships
            row_groups,
            row_group_sorts: row_group_sorts.length > 0 ? row_group_sorts : undefined,
            values,
            variables: (() => {
              const completeVars = variables.filter((v) => v.name && v.default_value);
              return completeVars.length > 0 ? completeVars : undefined;
            })(),
          },
          versionSelections: versionSelections && Object.keys(versionSelections).length > 0 ? versionSelections : undefined,
          limit: params.limit,
          offset: params.offset,
          ...(params.sort_by && { sort_by: params.sort_by }),
          ...(params.sort_dir && { sort_dir: params.sort_dir }),
          ...(params.filters && params.filters.length > 0 && { filters: params.filters }),
          ...(params.search && { search: params.search }),
        }
      });

      // Check for cardinality exceeded error (HTTP 400 with code: CARDINALITY_EXCEEDED)
      if (!res.ok) {
        const errorResponse = await res.json();

        // Handle cardinality error specially - show inline warning instead of throwing
        if (errorResponse && 'code' in errorResponse && errorResponse.code === 'CARDINALITY_EXCEEDED') {
          setCardinalityError(errorResponse as LinkedTableCardinalityError);
          // Return empty data grid response to prevent error boundary
          return {
            data: [],
            pagination: {
              total: 0,
              limit: params.limit || 20,
              offset: params.offset || 0,
              remaining: 0,
            },
          };
        }

        // For other errors, clear cardinality error
        setCardinalityError(null);

        // Extract available_fields from error response if present — the sidebar
        // needs field metadata to display names even when the query fails
        if (Array.isArray(errorResponse?.available_fields) && onFieldsChange) {
          const availableFieldsConverted: import('@loader/shared-types').TableFieldApiResponse[] = errorResponse.available_fields
            .filter((field: unknown): field is { id: string; table_id: string; name: string; data_type: string; table_alias?: string } =>
              typeof field === 'object' && field !== null && 'id' in field && 'name' in field
            )
            .map((field: { id: string; table_id: string; name: string; data_type: string; table_alias?: string }) => ({
              id: field.id,
              table_id: field.table_id,
              name: field.name,
              data_type: field.data_type as import('@loader/shared-types').TableFieldApiResponse['data_type'],
              is_required: false,
              is_unique: false,
              is_enabled: true,
              order_index: 0,
              description: null,
              default_value: null,
              validation_rules: null,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              table_alias: field.table_alias,
            }));
          onFieldsChange(availableFieldsConverted);
        }

        // Extract readable error message — Zod validation returns an array of error objects
        const rawError = errorResponse?.error;
        const errorMessage = Array.isArray(rawError)
          ? rawError.map((e: { message?: string }) => e.message).filter(Boolean).join('; ') || 'Validation failed'
          : rawError || 'Failed to load preview data';
        throw new Error(String(errorMessage));
      }

      // Success - clear any previous cardinality error
      setCardinalityError(null);

      const response = await res.json();

      // Check for error response (backend returns { error: string } on failure)
      if ('error' in response) {
        throw new Error(response.error || 'Failed to load preview data');
      }

      // Backend returns data directly without a wrapper
      // Response format matches LinkedTablePreviewResponse from shared-types
      const apiResponse = response as LinkedTablePreviewResponse;

      // Store fields metadata for column generation (fields for data table rendering)
      if (apiResponse.fields) {
        setFieldsMetadata(apiResponse.fields);
      }

      // Notify parent of all available fields (for column picker in sidebar)
      // Use available_fields which includes ALL fields from all tables, not just result fields
      if (apiResponse.available_fields && onFieldsChange) {
        const availableFieldsConverted: import('@loader/shared-types').TableFieldApiResponse[] = apiResponse.available_fields.map((field) => ({
          id: field.id,
          table_id: field.table_id,
          name: field.name,
          data_type: field.data_type as import('@loader/shared-types').TableFieldApiResponse['data_type'],
          is_required: false,
          is_unique: false,
          is_enabled: true,
          order_index: 0,
          description: null,
          default_value: null,
          validation_rules: null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          table_alias: field.table_alias,
        }));
        onFieldsChange(availableFieldsConverted);
      }

      // Notify parent if backend returned normalized config (config cleanup occurred)
      if (apiResponse.normalized_config && onConfigNormalized) {
        onConfigNormalized(
          apiResponse.normalized_config,
          apiResponse.normalized_config._changes
        );
      }

      // Return DataGridResponse format
      // Calculate remaining items for pagination
      const total = apiResponse.pagination.total;
      const offset = apiResponse.pagination.offset;
      const limit = apiResponse.pagination.limit;
      const remaining = Math.max(0, total - (offset + limit));

      return {
        data: apiResponse.data,
        pagination: {
          ...apiResponse.pagination,
          remaining,
        },
      };
    },
    initialPageSize: 20,
    initialSortBy: 'created_at',
    initialSortDir: 'desc',
    queryKeyPrefix: 'linkedTables',
    // Use memoized extraParams with stable reference
    extraParams,
    // Aggressive refetch but keep data during transition to prevent flashing
    staleTime: 0, // Always refetch (data is stale immediately)
    cacheTime: 1000, // Keep old data for 1 second during refetch to prevent flash
    refetchOnWindowFocus: false,
    // Handle errors inline instead of throwing to error boundary
    throwOnError: false,
  });

  // ============================================================================
  // Column Generation
  // ============================================================================

  /**
   * Build complete field schema including calculated columns
   * Fields are fetched from the preview response, so we just need to add calculated columns
   */
  const fieldsFromMetadata = useMemo((): import('@loader/shared-types').TableFieldApiResponse[] => {
    // Convert fields metadata to TableFieldApiResponse format
    // Note: fieldsMetadata already includes calculated columns from the API
    // (with "calculated." prefix), so we don't need to add them separately
    const fields: import('@loader/shared-types').TableFieldApiResponse[] = fieldsMetadata.map((field) => ({
      id: field.id,
      table_id: linkedTable?.tables?.[0]?.target_id || '',
      name: field.name,
      data_type: field.data_type as import('@loader/shared-types').TableFieldApiResponse['data_type'],
      is_required: false,
      is_unique: false,
      is_enabled: true,
      order_index: 0,
      description: null,
      default_value: null,
      validation_rules: null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      ...(field.table_alias && { table_alias: field.table_alias }),
    }));

    return fields;
  }, [fieldsMetadata, linkedTable?.tables]);

  // Note: onFieldsChange is now called directly in the query function with available_fields
  // This ensures the sidebar always has access to ALL fields from all tables, not just result fields

  /**
   * Extract business data from preview records
   * The API returns records with a payload field containing the actual data
   */
  const dataRecords = useMemo(() => {
    return grid.data.map((record) => {
      return (record.payload as Record<string, unknown>) || record;
    });
  }, [grid.data]);

  /**
   * Track whether we have data (stable boolean for dependency tracking)
   */
  const hasData = dataRecords.length > 0;

  /**
   * Generate table columns from schema
   *
   * CRITICAL PERFORMANCE NOTE:
   * - Depends on fieldsFromMetadata (schema structure), NOT on dataRecords array
   * - This prevents infinite re-renders when data changes but structure doesn't
   * - hasData is a stable boolean that only changes once (false -> true)
   * - We access dataRecords[0] inside but don't include dataRecords in deps
   *   because the structure (what columns exist) only changes when schema changes
   *
   * Process:
   * 1. Use first row as sample data
   * 2. Generate columns from schema using fieldsFromMetadata
   * 3. All columns are visible (no hidden columns in new design)
   */
  const columnDefs = useMemo((): ColumnDef<Record<string, unknown>>[] => {
    // Need BOTH data AND field metadata to generate columns
    if (!hasData) {
      return [];
    }

    if (fieldsFromMetadata.length === 0) {
      return [];
    }

    // Use ONLY the first row for column structure (not all data)
    const firstRow = dataRecords[0];
    const sampleData = [firstRow];
    // Use fieldsFromMetadata which has the correct order from the backend
    const allColumns = generateColumnsFromSchema(sampleData, fieldsFromMetadata);

    // All columns are visible (no hidden columns in new design)
    return allColumns;
    // Dependencies:
    // - fieldsFromMetadata: Schema structure (fields added/removed/changed)
    // - hasData: Data availability (at least one row)
    // Note: We intentionally don't include dataRecords to prevent infinite loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldsFromMetadata, hasData]);

  // ============================================================================
  // Render
  // ============================================================================

  // Show query errors inline (instead of throwing to error boundary)
  // This allows users to modify their configuration to fix the issue
  if (grid.error) {
    return (
      <QueryErrorDisplay error={grid.error} />
    );
  }

  // Show cardinality warning inline (not in error boundary)
  // This allows users to modify their configuration to fix the issue
  // But show loading state if query is refetching (user changed config)
  if (cardinalityError) {
    if (grid.isFetching) {
      // User changed config, show loading while refetching
      return (
        <div className="flex h-full items-center justify-center">
          <div className="text-muted-foreground flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Loading...</span>
          </div>
        </div>
      );
    }
    return (
      <CardinalityWarning error={cardinalityError} />
    );
  }

  // Empty state when no row_groups or values configured
  if (isEmpty) {
    return (
      <div className="flex h-full flex-col items-center justify-center text-muted-foreground">
        <Columns3 className="h-12 w-12 mb-4 opacity-50" />
        <p className="text-lg font-medium">Start building your view</p>
        <p className="text-sm">Drag columns to Row Groups or Values to see data</p>
      </div>
    );
  }

  return (
    <div className="h-full w-full">
      <DataTable
        data={dataRecords}
        columns={columnDefs}
        // Server-side pagination only
        pageIndex={grid.pagination.pageIndex}
        pageSize={grid.pagination.pageSize}
        totalItems={grid.pagination.total}
        pageCount={grid.pagination.pageCount}
        onPageChange={grid.actions.setPage}
        onPageSizeChange={grid.actions.setPageSize}
        // Loading states
        isLoading={grid.isLoading}
        isFetching={grid.isFetching}
        // Disable filtering - managed by sidebar config via SQL-native API
        enableFiltering={false}
        enablePagination={true}
        showFooter={true}
        // Visual variant
        variant={variant}
      />
    </div>
  );
};
